import styled from 'styled-components';
import { DefaultTheme } from 'styled-components';
import { LabelPosition } from './index';

export const InputContainer = styled.div<{ width?: string; labelPosition?: LabelPosition }>`
  display: flex;
  flex-direction: ${({ labelPosition }) => labelPosition === 'left' ? 'row' : 'column'};
  align-items: ${({ labelPosition }) => labelPosition === 'left' ? 'center' : 'flex-start'};
  width: ${(props) => props.width || '100%'};
  position: relative;
  gap: ${({ labelPosition }) => labelPosition === 'left' ? '1rem' : '0.5rem'};
`;

export const InputLabel = styled.label<{ required?: boolean; labelPosition?: LabelPosition }>`
  margin-bottom: ${({ labelPosition }) => labelPosition === 'top' ? '0.5rem' : '0'};
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }: { theme: DefaultTheme }) => theme.darkColor};
  width: ${({ labelPosition }) => labelPosition === 'left' ? '100px' : 'auto'};
  text-align: ${({ labelPosition }) => labelPosition === 'left' ? 'right' : 'left'};

  ${({ required, theme }: { required?: boolean; theme: DefaultTheme }) => required && `
    &::after {
      content: '*';
      color: ${theme.dangerColor};
      margin-left: 0.25rem;
    }
  `}
`;

export const InputGroup = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
`;

export const Input = styled.input`
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 0.375rem 0 0 0.375rem;
  font-size: 1rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.darkColor};
  background-color: ${({ theme }) => theme.white};
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.defaultColor};
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
  }
`;

export const CalendarButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-left: none;
  border-radius: 0 0.375rem 0.375rem 0;
  background-color: ${({ theme }) => theme.defaultColor};
  color: #fff;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
  width: 42px;
  height: 42px;

  &:hover {
    background-color: ${({ theme }) => theme.defaultColorHover};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
  }
`;

export const CalendarContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 0.5rem;
  background-color: ${({ theme }) => theme.white};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 50;
  width: 320px;
`;

export const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
`;

export const MonthTitle = styled.h3`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.darkColor};
`;

export const NavigationButton = styled.button`
  padding: 0.5rem;
  background: none;
  border: none;
  color: ${({ theme }) => theme.darkColorLight};
  cursor: pointer;
  transition: color 0.15s ease-in-out;

  &:hover {
    color: ${({ theme }) => theme.darkColor};
  }

  &:focus {
    outline: none;
  }
`;

export const CalendarTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  padding: 0.5rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
`;

export const CalendarHeaderCell = styled.th`
  padding: 0.5rem;
  text-align: center;
  font-weight: bold;
  font-size: 0.75rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  background-color: ${({ theme }) => theme.lightColor};
`;

export const CalendarCell = styled.td<{
  isSelected?: boolean;
  isToday?: boolean;
}>`
  padding: 0.5rem;
  text-align: center;
  cursor: pointer;
  font-size: 0.75rem;
  color: ${(props) => (props.isSelected ? props.theme.white : props.theme.darkColor)};
  background-color: ${(props) =>
    props.isSelected
      ? props.theme.defaultColor
      : props.isToday
      ? props.theme.lightColor
      : 'transparent'};
  border-radius: 0.25rem;
  border: 1px solid ${({ theme }) => theme.borderColor};

  &:hover {
    background-color: ${(props) =>
      props.isSelected ? props.theme.defaultColorHover : props.theme.lightColor};
  }
`;

export const CalendarFooter = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const ActionButton = styled.button<{
  variant: 'primary' | 'danger' | 'success';
}>`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;

  ${(props) => {
    switch (props.variant) {
      case 'primary':
        return `
          background-color: ${props.theme.infoColor};
          color: #fff;
          font-weight: 700;
          &:hover {
            background-color: ${props.theme.infoColorHover};
          }
        `;
      case 'danger':
        return `
          background-color: ${props.theme.dangerColor};
          color: #fff;
          font-weight: 700;
          &:hover {
            background-color: ${props.theme.dangerColorHover};
          }
        `;
      case 'success':
        return `
          background-color: ${props.theme.successColor};
            color: #fff;
          font-weight: 700;
          &:hover {
            background-color: ${props.theme.successColorHover};
          }
        `;
    }
  }}
`;
